import { Icon } from '@/shared/ui/icon'
import { useStyles } from './styles'
import { EarnedPoints } from '@/shared/ui/earnedPoints'
import { TaskTexts, TaskItem } from '@/entities/tasks/model/types'
import { useState } from 'react'
import { $isUpdateProgressAvailable, $userLanguage, $userToken } from '@/entities/user'
import { $epic, $steam } from '@/entities/platforms/store'
import { $steamUserData } from '@/entities/platforms/steam/store'
import { useUnit } from 'effector-react'
import { lang } from '@/shared/constants/lang'
import { usePostStory } from '@/shared/hooks/usePostStory'

import { getTasksFx, checkTaskFx } from '@/entities/tasks/model/effects'
import { goToAuth, goToCase, goToConnectPlatform, goToTon } from '@/app/routing/events'

import WebApp from '@twa-dev/sdk'
import { useTonConnectUI } from '@tonconnect/ui-react'
import { getUserProfileFx } from '@/entities/user'
import { isHomeScreenCompatible } from '@/shared/util/isHomeScreenCompatible'
import { steamOnTasks, TaskImageObject } from '@/widgets/tasks/utils'
import { PlayGameModal } from './ui'
import { useHideNav } from '@/shared/hooks/useHideNav'
import { getSteamRefreshFx } from '@/entities/platforms/steam'

interface TaskProps extends TaskItem {
  isNews?: boolean
}

const Task = (props: TaskProps) => {
  const { name, reward, isCompleted, variant, payload, isNews } = props
  const isDynamicTask = name.slice(0, 4) === 'play'
  const _icon_name: keyof TaskTexts = isDynamicTask ? ('steam' as keyof TaskTexts) : name
  const { classes } = useStyles()
  const userToken = useUnit($userToken)
  const userLang = useUnit($userLanguage)
  const isUpdateAvailable = useUnit($isUpdateProgressAvailable)
  const steamStatus = useUnit($steam).status
  const epicStatus = useUnit($epic).status
  const popupTexts = lang[userLang].webappPopup
  const isTonConnected = useTonConnectUI()[0].connected
  const userData = useUnit($steamUserData)
  const referredUsersAmount = userData?.referal_info.referred_level_1
  const { isMakingStoryLink, handleShareStory } = usePostStory()
  const [isLoading, setIsLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  useHideNav(isModalVisible)

  const isSteamOnTask = steamOnTasks.includes(name)

  const handleVote = async () => {
    if (payload?.url) {
      handleTgLink(payload?.url)
      pushTask()
    }
  }

  const pushTask = async () => {
    await checkTaskFx({ token: userToken!, task_id: props.id })
    await getTasksFx({ token: userToken! })
    await getUserProfileFx(userToken!)
    setIsLoading(false)
  }

  const handleModalConfirm = async () => {
    await getSteamRefreshFx(userToken!).then(async () => {
      setIsModalVisible(false)
      setIsLoading(true)
      await pushTask()
    })
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
  }

  const handleAddSteam = async () => {
    if (steamStatus !== 'Connected') {
      goToConnectPlatform({ platform: 'steam' })
      return
    }
    return await pushTask()
  }
  const handleAddEpic = async () => {
    if (epicStatus !== 'Connected') {
      goToConnectPlatform({ platform: 'egs' })
      return
    }
    return await pushTask()
  }

  const handlePostStory = async () => {
    await handleShareStory()

    setIsLoading(false)
    return
  }

  const handleUpdateProgress = async () => {
    if (steamStatus !== 'Connected') {
      goToConnectPlatform({ platform: 'steam' })
      return
    }
    return await pushTask()
  }

  const handleTgLink = async (url: string) => {
    WebApp.openTelegramLink(url)
    return await pushTask()
  }
  const handleLink = async (url: string) => {
    WebApp.openLink(url)
    return await pushTask()
  }

  const handleFriends = async () => {
    if (referredUsersAmount! < props.payload.maxCount!) {
      WebApp.showPopup({ title: popupTexts.error, message: popupTexts.inviteMoreFriends })
      return setIsLoading(false)
    }
    return await pushTask()
  }

  const handleAddToHomeScreen = async () => {
    if (!isHomeScreenCompatible(WebApp.platform)) {
      WebApp.showPopup({ title: popupTexts.error, message: popupTexts.notSupportedOnDevice })
      return setIsLoading(false)
    }

    WebApp.addToHomeScreen()
    return await pushTask()
  }

  const handleAddEmoji = async () => {
    WebApp.setEmojiStatus('5422600651643314130', {}, async (isSet) => {
      if (isSet) {
        return await pushTask()
      } else {
        setIsLoading(false)
      }
    })
  }

  const handleTonConnect = async () => {
    if (!isTonConnected) {
      return goToTon()
    }
    return await pushTask()
  }

  const handleGame = async () => {
    if (steamStatus === 'Available') {
      goToConnectPlatform({ platform: 'steam' })
      return
    }
    if (isUpdateAvailable) {
      setIsLoading(false)
      return setIsModalVisible(true)
    } else {
      return await pushTask()
    }
  }

  const handleTaskStart = async (name: string) => {
    console.log(name, '<')
    setIsLoading(true)
    if (!userToken) return goToAuth()
    if (name === 'addEmoji') return await handleAddEmoji()
    if (name === 'tonConnect') return await handleTonConnect()
    if (name === 'addToHomeScreen') return await handleAddToHomeScreen()
    if (name === 'boost') return await handleTgLink(userLang == 'en' ? props.payload.url! : props.payload.url_cis!)
    if (name === 'streaks') return await handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'tApps') return await handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'gamedCommunity') return await handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'gamedChat') return await handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'addSteam') return await handleAddSteam()
    if (name === 'addEpic') return await handleAddEpic()
    if (name === 'friends1') return await handleFriends()
    if (name === 'friends3') return await handleFriends()
    if (name === 'friends5') return await handleFriends()
    if (name === 'updateProgress') return await handleUpdateProgress()
    if (name === 'postStory') return await handlePostStory()
    if (name.slice(0, 4) === 'play') return await handleGame()
    if (name === 'x' || name === 'xPost') return await handleLink(payload.url!)
    if (name === 'openSteamOn') return goToCase({ id: 14 })
    if (name === 'openPs5') return goToCase({ id: 16 })
    if (name === 'vote') return handleVote()
    if (name === 'yesCoin') return handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'joinUpscale') return handleLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'tradeStormTrade') return handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'tradeUpscale') return handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'boinkersSlut') return handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'boinkersMoon') return handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    if (name === 'pokergram') return handleTgLink(userLang == 'en' ? payload.url! : payload.url_cis!)
    else pushTask()
  }

  const texts = lang[userLang].pages.tasks

  const renderTaskCounter = () => {
    if (variant !== 'counter') {
      return null
    }
    const counter = `${props.currentCount!} / ${props.maxCount!}`

    return (
      <div className={classes.counterWrapper}>
        {isLoading ? (
          <div className={classes.throbberWrapper}>
            <Icon
              view="throbber"
              width={12}
            />
          </div>
        ) : (
          <span className={classes.counterField}>{counter}</span>
        )}
      </div>
    )
  }

  const renderTaskCompletion = () => {
    if (variant !== 'completion') return null
    if (isLoading || (isMakingStoryLink && name === 'postStory')) {
      return (
        <div className={classes.throbberWrapper}>
          <Icon view="throbber" />
        </div>
      )
    }
    if (props.isCompleted) {
      return (
        <div className={classes.throbberWrapper}>
          <Icon
            view="checked"
            width={16}
          />
        </div>
      )
    }

    return (
      <div className={classes.throbberWrapper}>
        <Icon
          view="roundedArrow"
          width={16}
        />
      </div>
    )
  }

  return (
    <>
      {isModalVisible && (
        <PlayGameModal
          onCancel={handleModalCancel}
          onConfirm={handleModalConfirm}
        />
      )}
      <div
        data-loading={isLoading}
        className={classes.root}
        data-pinned={props.pinned}
        data-isnews={isNews}
        data-issteamon={isSteamOnTask}
        data-iscompleted={isCompleted}
        onClick={() => handleTaskStart(name)}
      >
        <div className={classes.iconWrapper}>
          <img
            src={`https://storage.yandexcloud.net/gamed/tasks/${TaskImageObject[_icon_name]}.webp`}
            alt=""
            className={classes.taskIcon}
          />
        </div>
        <div
          className={classes.textWrapper}
          data-isnews={isNews}
          data-pinned={props.pinned}
          data-center={!texts.description[name] || !texts.name[name]}
        >
          <div className={classes.headingWrapper}>
            {!isDynamicTask ? (
              <span
                className={classes.heading}
                data-isnews={isNews}
              >
                {texts.name[name]}
              </span>
            ) : (
              <span
                className={classes.heading}
                data-ellipsis
              >
                {texts.name.play}
                {payload?.name}
              </span>
            )}

            {reward > 0 && <EarnedPoints points={reward} />}
          </div>
          {!isDynamicTask ? (
            <span
              className={classes.description}
              data-isnews={isNews}
            >
              {texts.description[name]}
            </span>
          ) : (
            <span className={classes.description}>
              {texts.description.play}
              {payload?.name}&nbsp;{texts.description.playDesc}
            </span>
          )}
        </div>
        {!isNews && renderTaskCompletion()}
        {!isNews && renderTaskCounter()}
      </div>
    </>
  )
}
export default Task
